import { SessionContext } from "@/context/SessionContext";
import { BarChart } from "@mantine/charts";
import { Box, Card, Group, NativeSelect, Radio, Slider, Text, useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { addDays, endOfMonth, format, isWithinInterval, startOfMonth } from "date-fns";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useResizeDetector } from "react-resize-detector";

const barColors = [
	"#2b2ef8", "#f72acb", "#3d90fc", "#d63dfc", "#52c2ff", "#b752ff", "#5ad6ff", "#a959ff",
	"#62e5ff", "#a061ff", "#9fe3f1", "#c1a0f2", "#c4eff7", "#d7c3f7", "#d4f3f9", "#e3d4fa", "#e2f7fb", "#ebe1fa",
];

type AnalyticType = "measures" | "stems" | "previews" | "performances";
type IntervalType = "day" | "week" | "month";

function getDataYears(songs: any[]): number[] {
	const years = new Set<number>();
	for (const song of songs) {
		for (const su of song.submissionUsage || []) {
			if (su.timestamp) years.add(new Date(su.timestamp).getFullYear());
		}
		for (const d of song.drags || []) {
			if (d.timestamp) years.add(new Date(d.timestamp).getFullYear());
		}
		for (const p of song.previews || []) {
			if (p.timestamp) years.add(new Date(p.timestamp).getFullYear());
		}
	}
	return Array.from(years).sort();
}

function getJan1ToDec31WeekRanges(year: number) {
	const yearStart = new Date(year, 0, 1);
	const yearEnd = new Date(year, 11, 31);
	let weekStart = new Date(yearStart.getTime());
	const weeks = [];
	let weekNum = 1;
	while (weekStart <= yearEnd) {
		const weekEnd = new Date(Math.min(addDays(weekStart, 6).getTime(), yearEnd.getTime()));
		weeks.push({
			key: `${format(weekStart, "yyyy-MM-dd")}_${format(weekEnd, "yyyy-MM-dd")}`,
			label: `${format(weekStart, "MM/dd")} - ${format(weekEnd, "MM/dd")}`,
			month: format(weekStart, "MMM"),
			weekNum,
			start: new Date(weekStart.getTime()),
			end: new Date(weekEnd.getTime()),
		});
		weekNum++;
		weekStart = addDays(weekEnd, 1);
	}
	return weeks;
}

function getFullYearMonths(year: number) {
	return Array.from({ length: 12 }).map((_, i) => {
		const firstDay = new Date(year, i, 1);
		return {
			key: format(firstDay, "yyyy-MM"),
			label: format(firstDay, "MMM"),
			start: new Date(startOfMonth(firstDay).getTime()),
			end: new Date(endOfMonth(firstDay).getTime()),
		};
	});
}

function getFullYearDays(year: number) {
	const days: { key: string; label: string; start: Date; end: Date }[] = [];
	const start = new Date(year, 0, 1);
	const end = new Date(year, 11, 31);
	let current = new Date(start.getTime());
	while (current <= end) {
		days.push({
			key: format(current, "yyyy-MM-dd"),
			label: format(current, "MM-dd"),
			start: new Date(current.getTime()),
			end: new Date(current.getTime()),
		});
		current = addDays(current, 1);
	}
	return days;
}

function getDateBinKey(
	date: Date | string | undefined | null,
	interval: IntervalType,
	weekBins: { key: string; start: Date; end: Date }[],
	monthBins: { key: string; start: Date; end: Date }[],
	dayBins: { key: string; start: Date; end: Date }[]
) {
	if (!date) return "";
	let dateObj: Date = typeof date === "string" ? new Date(date) : new Date(date.getTime());
	dateObj = new Date(dateObj.getTime());
	dateObj.setHours(0, 0, 0, 0);
	if (isNaN(dateObj.getTime())) return "";

	if (interval === "week") {
		for (const week of weekBins) {
			if (dateObj >= week.start && dateObj <= week.end) {
				return week.key;
			}
		}
		return "";
	}
	if (interval === "day") {
		for (const day of dayBins) {
			if (day.start.getTime() === dateObj.getTime()) {
				return day.key;
			}
		}
		return "";
	}
	if (interval === "month") {
		for (const month of monthBins) {
			if (dateObj >= month.start && dateObj <= month.end) {
				return month.key;
			}
		}
		return "";
	}
	return "";
}

const AnalyticsChart: React.FC = () => {
	const theme = useMantineTheme();
	const session = useContext(SessionContext);
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);
	const today = new Date();

	const artistStats = session?.artistStats;
	const { ref } = useResizeDetector();
	const [analytic, setAnalytic] = useState<AnalyticType>("measures");
	const [interval, setInterval] = useState<IntervalType>("month");

	const dataYears = useMemo(() =>
		artistStats?.songs ? getDataYears(artistStats.songs) : [today.getFullYear()],
		[artistStats, today]
	);
	const [selectedYear, setSelectedYear] = useState<number>(dataYears[dataYears.length - 1] || today.getFullYear());

	const weekBins = useMemo(() => getJan1ToDec31WeekRanges(selectedYear), [selectedYear]);
	const monthBins = useMemo(() => getFullYearMonths(selectedYear), [selectedYear]);
	const dayBins = useMemo(() => getFullYearDays(selectedYear), [selectedYear]);
	const weeksInYear = weekBins.length;

	// Default week window: last 4 weeks ending with the current week
	const defaultWeekIndex = useMemo(() => {
		const idx = weekBins.findIndex(
			week =>
				isWithinInterval(today, { start: week.start, end: week.end })
		);
		return Math.max(0, Math.min(idx, weeksInYear - 1) - 3);
	}, [weekBins, today, weeksInYear]);

	// Default day window: last 7 days ending with today
	const defaultDayIndex = useMemo(() => {
		const idx = dayBins.findIndex(
			day =>
				day.start.getTime() === new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime()
		);
		return Math.max(0, Math.min(idx, dayBins.length - 1) - 6);
	}, [dayBins, today]);

	const [weekWindowStart, setWeekWindowStart] = useState(defaultWeekIndex);
	const [dayWindowStart, setDayWindowStart] = useState(defaultDayIndex);

	useEffect(() => {
		if (interval === "week") {
			setWeekWindowStart(defaultWeekIndex);
		}
		if (interval === "day") {
			setDayWindowStart(defaultDayIndex);
		}
	}, [interval, selectedYear, defaultWeekIndex, defaultDayIndex]);

	const visibleBins = useMemo(() => {
		if (interval === "week") {
			return weekBins.slice(weekWindowStart, weekWindowStart + 4);
		}
		if (interval === "day") {
			return dayBins.slice(dayWindowStart, dayWindowStart + 7);
		}
		if (interval === "month") {
			return monthBins;
		}
		return [];
	}, [interval, weekBins, weekWindowStart, dayBins, dayWindowStart, monthBins]);

	const groupedData = useMemo(() => {
		if (!artistStats?.songs) return {};

		let rows: { binKey: string, songName: string, value: number }[] = [];

		for (const song of artistStats.songs) {
			switch (analytic) {
				case "measures":
					for (const su of song.submissionUsage || []) {
						if (!su.measuresUsed || !su.projectSubmissionId) continue;
						const dateKey = getDateBinKey(su.timestamp, interval, weekBins, monthBins, dayBins);
						if (!dateKey) continue;
						rows.push({ binKey: dateKey, songName: song.songName, value: su.measuresUsed });
					}
					break;
				case "stems":
					for (const d of song.drags || []) {
						if (!d.measureCount) continue;
						const dateKey = getDateBinKey(d.timestamp, interval, weekBins, monthBins, dayBins);
						if (!dateKey) continue;
						rows.push({ binKey: dateKey, songName: song.songName, value: d.measureCount });
					}
					break;
				case "previews":
					for (const p of song.previews || []) {
						const dateKey = getDateBinKey(p.timestamp, interval, weekBins, monthBins, dayBins);
						if (!dateKey) continue;
						rows.push({ binKey: dateKey, songName: song.songName, value: 1 });
					}
					break;
				case "performances":
					for (const p of song.submissionUsage || []) {
						const dateKey = getDateBinKey(p.timestamp, interval, weekBins, monthBins, dayBins);
						if (!dateKey) continue;
						rows.push({ binKey: dateKey, songName: song.songName, value: 1 });
					}
					break;
			}
		}

		const grouped: { [binKey: string]: { [songName: string]: number } } = {};
		for (const { binKey, songName, value } of rows) {
			if (!grouped[binKey]) grouped[binKey] = {};
			if (!grouped[binKey][songName]) grouped[binKey][songName] = 0;
			grouped[binKey][songName] += value;
		}

		return grouped;
	}, [artistStats, analytic, interval, weekBins, monthBins, dayBins]);

	const chartData = useMemo(() => {
		return visibleBins.map((bin) => ({
			date: bin.label,
			...((groupedData[bin.key] as any) || {})
		}));
	}, [visibleBins, groupedData]);

	const songKeys = useMemo(() => {
		if (!Array.isArray(chartData) || chartData.length === 0) return [];
		return Array.from(
			new Set(
				chartData.flatMap(obj =>
					Object.keys(obj).filter(key => key !== "date")
				)
			)
		);
	}, [chartData]);

	// --- Highlight x-label for the current day or week ---
	const isCurrentLabel = (label: string) => {
		if (interval === "day") {
			// Day label format is "MM-dd"
			return label === format(today, "MM-dd");
		}
		if (interval === "week") {
			// Find the label for the week that includes today
			const currentWeek = weekBins.find(
				(w) => today >= w.start && today <= w.end
			);
			return currentWeek ? label === currentWeek.label : false;
		}
		return false;
	};

	const CustomXAxisTick = (props: any) => {
		const { x, y, payload } = props;
		const highlighted = isCurrentLabel(payload.value);
		return (
			<text
				x={x}
				y={y + 10}
				textAnchor="middle"
				fontWeight={isMobile ? 300 : 500}
				fontSize={isMobile ? 7 : 14}
				fill={highlighted ? theme.colors.pink[4] : theme.colors.blue[1]}
			>
				{payload.value}
			</text>
		);
	};

	const xAxisTickFormatter = (label: string) => label;

	const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
	const evenlySpacedMonthMarks = months.map((month, i) => ({
		value: Math.round((i / 11) * (weeksInYear - 4)),
		label: month,
	}));

	if (!artistStats) {
		return <Text>Loading artist stats...</Text>;
	}

	return (
		<Card
			bg={theme.colors.blue[8]}
			h={isMobile ? 400 : 600}
			p="md"
			display="flex"
			radius="md"
			style={{ position: "relative" }}
		>
			<Box ref={ref} w={"100%"}>
				<Group mb={25} w="100%">
					<NativeSelect
						w={isMobile ? 100 : 200}
						label="Analytic"
						c={theme.colors.blue[0]}
						data={[
							{ value: "measures", label: "Measures Submitted" },
							{ value: "stems", label: "Measures Dragged" },
							{ value: "previews", label: "Previews" },
							{ value: "performances", label: "Performances" },
						]}
						value={analytic}
						onChange={(event) => setAnalytic(event.currentTarget.value as AnalyticType)}
					/>
					<NativeSelect
						w={isMobile ? 100 : 150}
						label="Interval"
						c={theme.colors.blue[0]}
						data={[
							{ value: "day", label: "Day" },
							{ value: "week", label: "Week" },
							{ value: "month", label: "Month" },
						]}
						value={interval}
						onChange={event => setInterval(event.currentTarget.value as IntervalType)}
					/>
					{dataYears.length > 1 && (
						<Box>
							<Text size="sm" mb={6} c={theme.colors.blue[0]} fw={500}>Year</Text>
							<Group gap="md">
								{dataYears.map(y => (
									<Radio
										key={y}
										value={String(y)}
										label={String(y)}
										size={isMobile ? "sm" : "md"}
										c={theme.colors.blue[0]}
										checked={selectedYear === y}
										onChange={() => setSelectedYear(y)}
									/>
								))}
							</Group>
						</Box>
					)}
					{interval === "week" && (
						<Box w={isMobile ? 140 : 320} mx="md">
							<Slider
								min={0}
								max={weeksInYear - 4}
								value={weekWindowStart}
								onChange={setWeekWindowStart}
								step={1}
								marks={evenlySpacedMonthMarks}
								label={val => {
									const startMeta = weekBins[val];
									const endMeta = weekBins[val + 3];
									if (!startMeta || !endMeta) return "";
									return `${format(startMeta.start, "MM/dd")} - ${format(endMeta.end, "MM/dd")}`;
								}}
								color={theme.colors.blue[5]}
								thumbSize={18}
								size={isMobile ? "sm" : "md"}
								w={isMobile ? "25vh" : "40vh"}
								styles={{
									markLabel: { color: theme.colors.blue[0], fontSize: isMobile ? 4 : 12 }
								}}
							/>
						</Box>
					)}
					{interval === "day" && (
						<Box w={isMobile ? 140 : 320} mx="md">
							<Slider
								min={0}
								max={dayBins.length - 7}
								value={dayWindowStart}
								onChange={setDayWindowStart}
								step={1}
								marks={[
									{ value: 0, label: format(dayBins[0].start, "MM/dd") },
									{ value: dayBins.length - 7, label: format(dayBins[dayBins.length - 1].start, "MM/dd") }
								]}
								label={val => {
									const start = dayBins[val].start;
									const end = dayBins[val + 6].start;
									return `${format(start, "MM/dd")} - ${format(end, "MM/dd")}`;
								}}
								color={theme.colors.blue[5]}
								thumbSize={18}
								size={isMobile ? "sm" : "md"}
								w={isMobile ? "25vh" : "40vh"}
								styles={{
									markLabel: { color: theme.colors.blue[0], fontSize: 12 }
								}}
							/>
						</Box>
					)}
				</Group>
				<BarChart
					h={isMobile ? 200 : 400}
					data={chartData}
					dataKey="date"
					type="stacked"
					tooltipAnimationDuration={200}
					series={songKeys.map((key, idx) => ({
						name: key,
						color: barColors[idx % barColors.length],
						stack: "a"
					}))}
					withTooltip
					legendProps={{
						verticalAlign: "bottom",
						height: 50,
					}}
					xAxisProps={{
						tick: CustomXAxisTick,
						stroke: theme.colors.blue[1],
						tickFormatter: xAxisTickFormatter
					}}
					yAxisProps={{
						tick: { fill: theme.colors.blue[1], fontWeight: 500, fontSize: isMobile ? 8 : 13 },
						stroke: theme.colors.blue[1],
					}}
					tooltipProps={{
						contentStyle: { color: theme.colors.blue[8], background: theme.colors.blue[0] },
						labelStyle: { color: theme.colors.blue[8] },
						itemStyle: { color: theme.colors.blue[8] },
					}}
					sx={{
						background: theme.colors.blue[8],
					}}
				/>
			</Box>
		</Card>
	);
};

export default AnalyticsChart;